import axios, { AxiosResponse } from "axios";
import { URLS } from "./constants";

declare global {
    interface Window {
        _satellite: any;
        digitalData: any;
    }
}

export default class OMEGA {
    private api_url: string;
    static instance: OMEGA;
    private constructor() {
        this.api_url = URLS.ORG_API_URL;
    }
    static init() {
        if (!OMEGA.instance) {
            OMEGA.instance = new OMEGA();
        }
        return OMEGA.instance;
    }
    updatePageTitle(title: string) {
        document.title = title;
    }
    isReady() {
        return window._satellite;
    }
    updateAndTrack(key: string, value: any, retryCount: number = 0) {
        try {
            let newDigitalData: any = window.digitalData || {},
                profile = sessionStorage.getItem('adobeid_ims_profile'),
                userProfile = profile && JSON.parse(profile);

        if (window.adobeIMS && window.adobeIMS?.isSignedInUser()) {
            // Ensure we have user profile data before proceeding
            if (!userProfile) {
                if (retryCount < 10) { // Max 10 retries (1 second total wait)
                    // Retry after a short delay to allow profile to be loaded
                    setTimeout(() => {
                        this.updateAndTrack(key, value, retryCount + 1);
                    }, 100);
                    return;
                } else {
                    // Fallback to anonymous tracking after max retries
                    newDigitalData = {
                        ...newDigitalData,
                        [key]: value
                    }
                    window.digitalData = newDigitalData;
                    this.track(key);
                    return;
                }
            }
            
            this.getCorpName(userProfile, (corpName: string) => {
                newDigitalData = {
                    ...newDigitalData,
                    ...this.updatUserProperties(corpName),
                    [key]: value
                }
                window.digitalData = newDigitalData;
                this.track(key);
            });
        } else {
            newDigitalData = {
                ...newDigitalData,
                [key]: value
            }
            window.digitalData = newDigitalData;
            this.track(key);
        }
        } catch (error) {
            console.error('OMEGA updateAndTrack error:', error);
            // Fallback tracking without user data
            try {
                window.digitalData = {
                    ...window.digitalData,
                    [key]: value
                };
                this.track(key);
            } catch (fallbackError) {
                console.error('OMEGA fallback tracking failed:', fallbackError);
            }
        }
    }
    updatUserProperties(corpName: string) {
        let lang, profile = sessionStorage.getItem('adobeid_ims_profile'),
            userProfile = profile && JSON.parse(profile), newDigitalData = {};
        corpName = corpName === "anonymous" ? '' : corpName;
        if (userProfile) {
            lang =
                userProfile?.preferred_languages?.length
                    ? userProfile.preferred_languages[0].replace("-", ":")
                    : "en:us";

            newDigitalData = {
                ...window.digitalData,
                user: {
                    id: userProfile.userId,
                    corpId: this.getCorpID(userProfile) || "anonymous",
                    corpName: corpName || userProfile.company || "anonymous",
                    authSystem: "ims",
                    accountType: "user",
                    language: lang,
                    auth: "authenticated",
                }
            };
        }

        return newDigitalData;
    }

    getCorpName(userProfile: any, callback: Function) {
        let corpID = this.getCorpID(userProfile) || "anonymous",
            orgName = sessionStorage.getItem(corpID);



        if (corpID === 'anonymous') {
           
            callback('anonymous');
            return 'anonymous';
        }
        
        if (orgName) {
          
            callback(orgName);
            return orgName;
        }

      
        return axios({
            url: this.api_url + corpID,
            headers: {
                'Authorization': 'Bearer ' + window.adobeIMS.getAccessToken().token
            },
            method: 'GET'
        })
            .then((res: AxiosResponse) => res.data)
            .then(function (OrgInfo: any) {
               
                if (OrgInfo && OrgInfo.orgName) {
                    sessionStorage.setItem(corpID, OrgInfo.orgName);
                    callback(OrgInfo.orgName);
                } else {
                  
                    callback('anonymous');
                }
            }).catch(function (error) {
               
                callback('anonymous');
            });
    }

    getCorpID(userProfile: any) {
      
        
        if (userProfile && userProfile.ownerOrg) {
           
            return userProfile.ownerOrg
        }

        if (userProfile && userProfile.projectedProductContext && userProfile.projectedProductContext.length) {
            const corpID = userProfile.projectedProductContext[0].prodCtx.owningEntity;
           
            return corpID;
        }
        
      
        return null;
    }
    updateDigitalDataUserInfo(): Promise<void> {
        return new Promise((resolve, reject) => {
            let profile = sessionStorage.getItem('adobeid_ims_profile'),
                userProfile = profile && JSON.parse(profile);

            if (window.adobeIMS && window.adobeIMS?.isSignedInUser()) {
                if (!userProfile) {
                    // If user is signed in but profile not loaded, try to get it
                    // This is a fallback - the primary flow should be in App.tsx onStateChanged
                  
                    window.adobeIMS.getProfile()
                        .then((fetchedProfile: any) => {
                            // Store in sessionStorage so other parts of the app can use it
                            sessionStorage.setItem('adobeid_ims_profile', JSON.stringify(fetchedProfile));
                            this.getCorpName(fetchedProfile, (corpName: string) => {
                                const updatedUserProperties = this.updatUserProperties(corpName);
                                window.digitalData = {
                                    ...window.digitalData,
                                    ...updatedUserProperties
                                };
                                resolve();
                            });
                        })
                        .catch((error: any) => {
                            console.error('OMEGA: Failed to fetch user profile:', error);
                            reject(error);
                        });
                } else {
                    this.getCorpName(userProfile, (corpName: string) => {
                        const updatedUserProperties = this.updatUserProperties(corpName);
                        window.digitalData = {
                            ...window.digitalData,
                            ...updatedUserProperties
                        };
                        resolve();
                    });
                }
            } else {
                resolve();
            }
        });
    }
    track(key: string) {
        try {
            if (window._satellite && typeof window._satellite.track === 'function') {
                window._satellite.track(key);
            } else {
                console.warn('OMEGA: _satellite.track not available');
            }
        } catch (error) {
            console.error('OMEGA track error:', error);
        }
    }

    /**
     * Enhanced method that ensures user data is updated before tracking
     * Use this method instead of updateAndTrack for better reliability
     */
    async updateAndTrackAsync(key: string, value: any): Promise<void> {
        try {
            // First ensure user data is updated if user is signed in
            if (window.adobeIMS && window.adobeIMS?.isSignedInUser()) {
                await this.updateDigitalDataUserInfo();
            }
            
            // Then perform the tracking with updated data
            this.updateAndTrack(key, value);
        } catch (error) {
            // Fallback to normal tracking if update fails
            this.updateAndTrack(key, value);
        }
    }
}
